import * as THREE from 'three';
import { WALL_HEIGHT } from './selectedAreaThreeModel.js';

export const WALL_CAP_CLIP_ALLOWANCE_METERS = 0.1;

/**
 * @param {number} maxWallHeightM
 * @returns {number}
 */
export function computeMaxVisibleHeightM(maxWallHeightM) {
  const wallHeight = Number.isFinite(maxWallHeightM) && maxWallHeightM > 0
    ? maxWallHeightM
    : WALL_HEIGHT;
  return wallHeight + WALL_CAP_CLIP_ALLOWANCE_METERS;
}

/**
 * @param {number} visibleHeightM
 * @returns {THREE.Plane}
 */
export function createFloorPlanVisibleHeightPlane(visibleHeightM) {
  return new THREE.Plane(new THREE.Vector3(0, -1, 0), visibleHeightM);
}

/**
 * @param {THREE.Plane} plane
 * @param {number} visibleHeightM
 */
export function updateFloorPlanVisibleHeightPlane(plane, visibleHeightM) {
  plane.constant = visibleHeightM;
}

/**
 * @param {{
 *   visibleHeightM: number;
 *   maxWallHeightM: number;
 * }} params
 */
export function createFloorPlanClippingState({ visibleHeightM, maxWallHeightM }) {
  const maxVisibleHeight = computeMaxVisibleHeightM(maxWallHeightM);
  const plane = createFloorPlanVisibleHeightPlane(visibleHeightM);
  return {
    visibleHeight: visibleHeightM,
    maxVisibleHeight,
    maxWallHeight: maxWallHeightM,
    plane,
    clippingPlanes: [plane],
  };
}

/**
 * @param {{ walls?: Array<THREE.Object3D> } | null | undefined} buildResult
 * @param {Record<string, unknown> | null | undefined} [_analysisJson]
 * @returns {number}
 */
export function getMaximumWallHeightM(buildResult, _analysisJson = null) {
  let maxY = 0;
  let hasGeometry = false;

  (buildResult?.walls || []).forEach((wallRoot) => {
    if (!wallRoot) return;
    const box = new THREE.Box3().setFromObject(wallRoot);
    if (box.isEmpty() || !Number.isFinite(box.max.y)) return;
    hasGeometry = true;
    maxY = Math.max(maxY, box.max.y);
  });

  if (!hasGeometry) {
    return WALL_HEIGHT;
  }

  return Math.max(maxY, WALL_HEIGHT);
}

/**
 * Clone a material and attach clipping planes without mutating the source.
 * @param {THREE.Material} material
 * @param {THREE.Plane[]} clippingPlanes
 * @returns {THREE.Material}
 */
export function cloneMaterialWithClipping(material, clippingPlanes) {
  const cloned = material.clone();
  cloned.clippingPlanes = clippingPlanes;
  cloned.clipIntersection = false;
  cloned.clipShadows = true;
  cloned.needsUpdate = true;
  return cloned;
}

/**
 * Apply clipping to a mesh by cloning its material.
 * @param {THREE.Mesh} mesh
 * @param {THREE.Plane[]} clippingPlanes
 * @returns {THREE.Material[]}
 */
export function applyClippingToMesh(mesh, clippingPlanes) {
  /** @type {THREE.Material[]} */
  const materials = [];
  if (!mesh?.material) return materials;

  if (Array.isArray(mesh.material)) {
    mesh.material = mesh.material.map((mat) => {
      const cloned = cloneMaterialWithClipping(mat, clippingPlanes);
      materials.push(cloned);
      return cloned;
    });
  } else {
    mesh.material = cloneMaterialWithClipping(mesh.material, clippingPlanes);
    materials.push(mesh.material);
  }
  return materials;
}

/**
 * Apply clipping to a line object by cloning its material.
 * @param {THREE.Line | THREE.LineSegments} line
 * @param {THREE.Plane[]} clippingPlanes
 * @returns {THREE.Material[]}
 */
export function applyClippingToLineObject(line, clippingPlanes) {
  /** @type {THREE.Material[]} */
  const materials = [];
  if (!line?.material) return materials;

  if (Array.isArray(line.material)) {
    line.material = line.material.map((mat) => {
      const cloned = cloneMaterialWithClipping(mat, clippingPlanes);
      materials.push(cloned);
      return cloned;
    });
  } else {
    line.material = cloneMaterialWithClipping(line.material, clippingPlanes);
    materials.push(line.material);
  }
  return materials;
}

/**
 * @param {THREE.Object3D} child
 * @returns {boolean}
 */
function shouldSkipClippableChild(child) {
  if (child.userData?.edgeSurface === 'floor') return true;
  if (child.userData?.skipVisibleHeightClip === true) return true;
  return false;
}

/**
 * Traverse an object and apply clipping to mesh and line descendants.
 * @param {THREE.Object3D} root
 * @param {THREE.Plane[]} clippingPlanes
 * @param {{ exclude?: (obj: THREE.Object3D) => boolean }} [options]
 * @returns {THREE.Material[]}
 */
export function applyClippingToClippableRoot(root, clippingPlanes, options = {}) {
  /** @type {THREE.Material[]} */
  const materials = [];
  if (!root) return materials;

  root.traverse((child) => {
    if (options.exclude?.(child)) return;
    if (shouldSkipClippableChild(child)) return;

    if (child instanceof THREE.Mesh) {
      materials.push(...applyClippingToMesh(child, clippingPlanes));
      return;
    }

    if (child instanceof THREE.LineSegments || child instanceof THREE.Line) {
      materials.push(...applyClippingToLineObject(child, clippingPlanes));
    }
  });

  return materials;
}

/**
 * @param {THREE.Object3D} root
 * @param {ReturnType<typeof createFloorPlanClippingState>} clippingState
 * @param {{ exclude?: (obj: THREE.Object3D) => boolean }} [options]
 * @returns {THREE.Material[]}
 */
export function applyClippingWithState(root, clippingState, options = {}) {
  return applyClippingToClippableRoot(root, clippingState.clippingPlanes, options);
}

/**
 * Traverse an object and apply clipping to all mesh descendants.
 * @param {THREE.Object3D} root
 * @param {THREE.Plane[]} clippingPlanes
 * @param {{ exclude?: (obj: THREE.Object3D) => boolean }} [options]
 * @returns {THREE.Material[]}
 */
export function applyClippingToObject(root, clippingPlanes, options = {}) {
  return applyClippingToClippableRoot(root, clippingPlanes, options);
}
